import React from 'react';
import {
  Box,
  TextField,
  Stack,
  Typography,
  Button,
  CircularProgress,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { addNote } from '../../../services/firebase';

interface AddNoteFormProps {
  caseId: string;
  onSuccess: () => void;
  onCancel?: () => void;
}

interface FormInputs {
  content: string;
}

const AddNoteForm: React.FC<AddNoteFormProps> = ({ caseId, onSuccess, onCancel }) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<FormInputs>();

  const onSubmit = async (data: FormInputs) => {
    try {
      await addNote(caseId, data.content);
      reset();
      onSuccess();
    } catch (error) {
      console.error('Error adding note:', error);
    }
  };

  return (
    <Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={3}>
          <Box>
            <Typography
              component="label"
              htmlFor="note-content"
              sx={{
                display: 'block',
                fontSize: '0.8125rem',
                fontWeight: 500,
                color: 'text.primary',
                mb: 1,
              }}
            >
              Note Content
            </Typography>
            <TextField
              id="note-content"
              multiline
              rows={4}
              fullWidth
              placeholder="Enter your note here…"
              {...register('content', {
                required: 'Note content is required',
                minLength: { value: 3, message: 'Note must be at least 3 characters long' },
              })}
              error={!!errors.content}
              helperText={errors.content?.message}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1.5,
              pt: 1,
            }}
          >
            {onCancel && (
              <Button
                type="button"
                variant="text"
                onClick={onCancel}
                disabled={isSubmitting}
                sx={{ minHeight: 40 }}
              >
                Cancel
              </Button>
            )}
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isSubmitting}
              sx={{ minHeight: 40, minWidth: 140 }}
              startIcon={
                isSubmitting ? <CircularProgress size={16} sx={{ color: '#FFFFFF' }} /> : null
              }
            >
              {isSubmitting ? 'Adding…' : 'Add Note'}
            </Button>
          </Box>
        </Stack>
      </form>
    </Box>
  );
};

export default AddNoteForm;
